import { alert } from '@codeleap/modals'
import { BaseChatPackage, isChatMarkableAsRead } from "../utils";
import { ReactQuery } from '@codeleap/query'
import { ChatChannelInstance, ListChatFilter, UpdatePushPreferencesBody } from "../types";
import { forceChatRerender, getUser, setToastMessage } from '../stores';

type UseChatsQueryOptions = {
  enabled?: boolean
  staleTime?: number
  refetchOnMount?: boolean | 'always'
  refetchOnWindowFocus?: boolean | 'always'
  retry?: boolean | number
  select?: (data: any) => any
  getNextPageParam?: (lastPage: any) => any
}

export class ChatsServices extends BaseChatPackage {
  private async applyLocalUnpin(channel: ChatChannelInstance) {
    if (channel?.state?.membership) {
      channel.state.membership = {
        ...channel.state.membership,
        // @ts-ignore
        pinned: false,
        // @ts-ignore
        pinned_at: undefined,
      } as any
    }

    if (channel?.data) {
      channel.data = {
        ...channel.data,
        // @ts-ignore
        pinned_at: undefined,
        // @ts-ignore
        pinned: false,
      } as any
    }

    // @ts-ignore
    if (channel?.state) {
      // @ts-ignore
      channel.state.pinned = false
      // @ts-ignore
      channel.state.pinned_at = undefined
    }

    // @ts-ignore
    channel.pinned = false
    // @ts-ignore
    channel.pinned_at = null

    const client = await this.getChatClient()
    const currentUser = getUser()

    client?.dispatchEvent?.({
      type: 'channel.updated',
      channel: {
        ...(channel.data || {}),
        pinned_at: null,
        pinned: false,
      },
      created_at: channel?.data?.created_at,
      last_message_at: channel?.data?.last_message_at,
      cid: channel.cid,
    } as any)

    client?.dispatchEvent?.({
      type: 'member.updated',
      cid: channel.cid,
      channel: {
        id: channel.id,
        type: channel.type,
      },
      member: {
        ...(channel.state?.membership || {}),
        user_id: currentUser?.id,
        pinned: false,
        pinned_at: null,
      } as any,
    } as any)

    forceChatRerender(channel.id)
  }

  private async applyLocalPin(channel: ChatChannelInstance) {
    const now = new Date().toISOString()
    if (channel?.state?.membership) {
      channel.state.membership = {
        ...channel.state.membership,
        // @ts-ignore
        pinned: true,
        // @ts-ignore
        pinned_at: now,
      } as any
    }

    if (channel?.data) {
      channel.data = {
        ...channel.data,
        // @ts-ignore
        pinned_at: now,
        // @ts-ignore
        pinned: true,
      } as any
    }

    // @ts-ignore
    if (channel?.state) {
      // @ts-ignore
      channel.state.pinned = true
      // @ts-ignore
      channel.state.pinned_at = now
    }

    // @ts-ignore
    channel.pinned = true
    // @ts-ignore
    channel.pinned_at = now

    const client = await this.getChatClient()
    const currentUser = getUser()

    client?.dispatchEvent?.({
      type: 'channel.updated',
      channel: {
        ...(channel.data || {}),
        pinned_at: now,
        pinned: true,
      },
      created_at: channel?.data?.created_at,
      last_message_at: channel?.data?.last_message_at,
      cid: channel.cid,
    } as any)

    client?.dispatchEvent?.({
      type: 'member.updated',
      cid: channel.cid,
      channel: {
        id: channel.id,
        type: channel.type,
      },
      member: {
        ...(channel.state?.membership || {}),
        user_id: currentUser?.id,
        pinned: true,
        pinned_at: now,
      } as any,
    } as any)

    forceChatRerender(channel.id)
  }

  useChats(filters: ListChatFilter, queryOptions?: UseChatsQueryOptions) {
    const query = ReactQuery.useInfiniteQuery({
      queryKey: ['chats', filters],
      queryFn: async ({ pageParam }) => {
        const { offset = 0, limit = 20 } = pageParam || {}

        return await this.chatApi.listChats({
          ...filters,
          limit,
          offset,
        })
      },
      initialPageParam: { offset: 0, limit: 20 },
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage.hasMore) return undefined

        const totalItems = allPages.reduce((acc, page) => acc + page.results.length, 0)

        return {
          offset: totalItems,
          limit: 20,
        }
      },
      enabled: queryOptions?.enabled !== false,
      staleTime: queryOptions?.staleTime,
      refetchOnMount: queryOptions?.refetchOnMount,
      refetchOnWindowFocus: queryOptions?.refetchOnWindowFocus,
      retry: queryOptions?.retry,
      select: queryOptions?.select,
    })

    const data = query.data?.pages.flatMap(page => page.results) ?? []

    const onEndReached = () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        query.fetchNextPage()
      }
    }

    const flatListProps = {
      data,
      onEndReached,
      loading: query.isFetching,
    }

    return {
      query,
      flatListProps,
    }
  }
  
  async toggleChatPin(channel: ChatChannelInstance, isPinned: boolean, options?: {
    onUnpinSuccess?: () => void
    onPinSuccess?: () => void
  }) {
    const handlePin = async (pinnedChannels: ChatChannelInstance[]) => {
      try {
        if (pinnedChannels?.length >= 3) {
          const sortedPinned = [...pinnedChannels].sort((a, b) => {
            const dateA = new Date(a.state.membership?.pinned_at || (a.data as any)?.pinned_at || 0).getTime()
            const dateB = new Date(b.state.membership?.pinned_at || (b.data as any)?.pinned_at || 0).getTime()
            return dateA - dateB
          })

          const oldestChannel = sortedPinned[0]

          if (oldestChannel) {
            await oldestChannel.unpin()
            await this.applyLocalUnpin(oldestChannel)
          }
        }

        await channel.pin()
        await this.applyLocalPin(channel)

        const pinnedAt = new Date().toISOString()
        if (channel?.state?.membership) {
          channel.state.membership.pinned_at = pinnedAt
        }

        options?.onPinSuccess?.()
      } catch (e) {
      }
    }

    try {
      if (isPinned) {
        const response = await channel.unpin()
        await this.applyLocalUnpin(channel)
        options?.onUnpinSuccess?.()
        return response
      } else {
        const chatClient = await this.getChatClient()
        const pinnedChannels = await chatClient.queryChannels({ pinned: true })
        if (pinnedChannels?.length >= 3) {
          alert.ask({
            title: 'Pin Limit Reached',
            body: 'You can only pin up to three chats at a time. Do you want to pin this chat anyway?',
            options: [
              { text: 'Cancel', style: 'cancel' },
              { text: "Pin chat" , onPress: () => handlePin(pinnedChannels) }
            ]
          })
          return Promise.resolve(null)
        }

        return handlePin(pinnedChannels)
      }
    } catch (e) {
      console.warn('Error toggle chat pin', e)
    }
  }

  async toggleReadChat(channel: ChatChannelInstance) {
    try {
      const isMarkableAsRead = isChatMarkableAsRead(channel)
      const user = getUser()

      if (isMarkableAsRead) {
        await channel.markRead()

        await channel.updatePartial({
          set: { 
            [`unread_${user?.id}`]: false
          }
        })
      } else {
        await channel.updatePartial({
          set: { 
            [`unread_${user?.id}`]: true
          }
        })
      }
      forceChatRerender(channel.id)
    } catch (e) {
      console.warn('Error toggle chat read status', e)  
    } finally {
        forceChatRerender(channel.id)
    }
  }

  async toggleMuteChat(channel: ChatChannelInstance, expirationInMinutes?: number) {
    if (!channel) return

    try {
      const muteStatus = channel.muteStatus()
      if (muteStatus?.muted) {
        const response = await channel.unmute()
        return response
      }

      let muteOptions: { expiration?: number } = {}
      
      if (expirationInMinutes && expirationInMinutes !== Infinity) {
        const expirationInMs = expirationInMinutes * 60 * 1000
        muteOptions = { expiration: expirationInMs }
      }

      const response = await channel.mute(muteOptions)
      
      if (response?.channel_mute?.expires && typeof window !== 'undefined' && (window as any).createMuteTimer) {
        ;(window as any).createMuteTimer(
          channel.id,
          channel.type || 'messaging',
          response.channel_mute.expires
        )
      }
      
      return response
    } catch (e) {
      console.warn('Error toggle chat mute status', e)  
    } finally {
      if (channel?.id) {
        forceChatRerender(channel.id)
      }
    }
  }

  async updatePushPreferences(body: UpdatePushPreferencesBody) {
    return this.chatApi.updatePushPreferences(body)
  }

  async deleteChatHistory(channel: ChatChannelInstance, channelName: string) {
    async function deleteHistory() {
      try {
        const user = getUser()

        const response = await channel.truncate({
          hard_delete: true,
          skip_push: true,
          user_id: user?.id,
        })  

        setToastMessage('Chat history has been cleared.')
        return response
      } catch (error) {
        console.error('Error deleting chat history:', error)
        setToastMessage('Failed to delete chat history.')
        throw error
      }
    }

    return alert.error({
      title: `Delete all messages from "${channelName}?"`,
      body: 'This chat will be empty for everyone but will remain in chat list.',
      options: [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: deleteHistory },
      ]
    })
  }

  async deleteChat(channel: ChatChannelInstance, channelName: string) {
    try {
      const handleDelete = () => {
        channel.delete()
      }

      return alert.error({
        title: `Delete chat "${channelName}?"`,
        body: 'This chat will be removed from the chat list and no longer accessible to any members.',
        options: [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Delete', style: 'destructive', onPress: handleDelete },
        ]
      })
    } catch (e) {
      console.error('Error deleting chat', e)
    }
  }
}
